import { motion } from "framer-motion";
import { CheckCircle2, AlertTriangle, XCircle, Building2, User, ShieldCheck, TrendingUp, Target, Layers, ExternalLink } from "lucide-react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip,
} from "recharts";
import { Cited, collectCitedNumbers } from "@/lib/citations";

// tiny helpers
const labelClass = (label) => {
  const l = (label || "").toLowerCase();
  if (l === "green") return "label-green";
  if (l === "amber") return "label-amber";
  return "label-red";
};
const labelIcon = (label) => {
  const l = (label || "").toLowerCase();
  if (l === "green") return <CheckCircle2 className="w-3.5 h-3.5" />;
  if (l === "amber") return <AlertTriangle className="w-3.5 h-3.5" />;
  return <XCircle className="w-3.5 h-3.5" />;
};
const ratingScore = (r) => ({ strong: 3, moderate: 2, weak: 1 })[(r || "").toLowerCase()] || 0;

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function Report({ report }) {
  const bt = report.inferred_business_type || "B2C";
  const intake = report.intake || {};
  const snippets = report.grounding_snippets || [];

  // Which snippet numbers does this report actually reference?
  const analysisPayload = {
    business_type_reason: report.business_type_reason,
    validation: report.validation,
    icp: report.icp,
    market: report.market,
    pricing: report.pricing,
    competition: report.competition,
  };
  const citedNumbers = collectCitedNumbers(analysisPayload);

  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-10" data-testid="report-root">
      {/* Header */}
      <motion.section variants={item} data-testid="report-header">
        <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">velo2one report</p>
        <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.02] text-zinc-900 mb-4">
          {intake.idea}
        </h1>
        <div className="flex flex-wrap gap-2 text-xs">
          <Tag>{bt}</Tag>
          <Tag>{intake.country}</Tag>
          {intake.target_market ? <Tag>Target: {intake.target_market}</Tag> : null}
        </div>
        {report.business_type_reason ? (
          <p className="mt-4 text-sm text-zinc-600 max-w-2xl leading-relaxed" data-testid="business-type-reason">
            <Cited snippets={snippets}>{report.business_type_reason}</Cited>
          </p>
        ) : null}
      </motion.section>

      {/* Nav */}
      <motion.nav variants={item} className="flex flex-wrap gap-2 sticky top-16 z-40 py-2 -mx-2 px-2 bg-[#FAFAF7]/80 backdrop-blur-md" data-testid="report-nav">
        {[
          ["validation", "Strength Signal"],
          ["icp", "ICP"],
          ["market", "Market"],
          ["pricing", "Pricing"],
          ["competition", "Competition"],
          ...(citedNumbers.length ? [["sources", "Sources"]] : []),
        ].map(([id, l]) => (
          <a key={id} href={`#${id}`} className="rounded-full px-4 py-1.5 text-xs font-semibold border border-zinc-300 hover:border-zinc-900 bg-white transition-colors">
            {l}
          </a>
        ))}
      </motion.nav>

      {/* 1. Idea Strength Signal */}
      <motion.section variants={item} id="validation" className="scroll-mt-32" data-testid="section-validation">
        <SectionHeader n="01" title="Idea Strength Signal" sub="Informative context, not a verdict. Whatever it shows, your full go-to-market plan comes next." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
          {[
            ["Problem realness", report.validation?.problem_realness],
            ["Monetization signal", report.validation?.monetization_signal],
            ["Differentiation", report.validation?.differentiation],
          ].map(([name, v]) => (
            <div key={name} className="velo-card p-6" data-testid={`validation-${name.split(' ')[0].toLowerCase()}`}>
              <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${labelClass(v?.label)}`}>
                {labelIcon(v?.label)} {v?.label || "unknown"}
              </div>
              <h3 className="font-bold text-lg mt-4 mb-2">{name}</h3>
              <p className="text-sm text-zinc-700 leading-relaxed">
                <Cited snippets={snippets}>{v?.evidence || "No evidence available."}</Cited>
              </p>
            </div>
          ))}
        </div>
        <StrengthFraming validation={report.validation} />
      </motion.section>

      {/* 2. ICP */}
      <motion.section variants={item} id="icp" className="scroll-mt-32" data-testid="section-icp">
        <SectionHeader n="02" title="Ideal Customer Profile" sub={
          bt === "B2C" ? "The one persona who feels the pain, has the budget, and is underserved." :
          bt === "B2B" ? "Three roles in the buying committee — plus firmographics." :
          "Four stakeholders in the government buying process."
        }/>
        <div className="mt-6">
          {bt === "B2C" && report.icp?.b2c_persona && <B2CPersona p={report.icp.b2c_persona} snippets={snippets} />}
          {bt === "B2B" && <B2BCommittee committee={report.icp?.b2b_committee || []} firm={report.icp?.b2b_firmographics} snippets={snippets} />}
          {bt === "B2G" && <B2GCommittee committee={report.icp?.b2g_committee || []} flag={report.icp?.b2g_flag} snippets={snippets} />}
        </div>
      </motion.section>

      {/* 3. Market */}
      <motion.section variants={item} id="market" className="scroll-mt-32" data-testid="section-market">
        <SectionHeader n="03" title="Market Readiness" sub={
          (report.market?.mode === "comparison")
            ? "Multiple candidate markets scored across five factors."
            : "Readiness snapshot for the market you specified."
        } />
        <MarketBlock market={report.market} snippets={snippets} />
      </motion.section>

      {/* 4. Pricing */}
      <motion.section variants={item} id="pricing" className="scroll-mt-32" data-testid="section-pricing">
        <SectionHeader n="04" title="Pricing" sub="Ranges, not fixed numbers. Anchored to comparables and your wedge." />
        <PricingBlock pricing={report.pricing} bt={bt} snippets={snippets} />
      </motion.section>

      {/* 5. Competition + Wedge */}
      <motion.section variants={item} id="competition" className="scroll-mt-32" data-testid="section-competition">
        <SectionHeader n="05" title="Competition & Your Wedge" sub="What already exists — and where you actually win." />
        <CompetitionBlock c={report.competition} snippets={snippets} />
      </motion.section>

      {/* 6. Sources (only numbers actually cited above) */}
      {citedNumbers.length > 0 && (
        <motion.section variants={item} id="sources" className="scroll-mt-32" data-testid="section-sources">
          <SectionHeader n="06" title="Sources" sub="Every citation above links to one of these. Numbers not listed here weren't referenced." />
          <ol className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sources-list">
            {citedNumbers.map((n) => {
              const s = snippets[n - 1];
              if (!s) return null;
              return (
                <li
                  key={n}
                  id={`src-${n}`}
                  className="velo-card p-5 flex gap-3"
                  data-testid={`source-${n}`}
                >
                  <span className="font-mono text-[#FF4757] text-sm font-bold shrink-0 pt-0.5">[{n}]</span>
                  <div className="min-w-0">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-sm font-semibold text-zinc-900 hover:text-[#FF4757] transition-colors inline-flex items-start gap-1"
                    >
                      <span className="line-clamp-2">{s.title || s.url}</span>
                      <ExternalLink className="w-3 h-3 mt-0.5 shrink-0 opacity-60" />
                    </a>
                    {s.snippet && (
                      <p className="text-xs text-zinc-600 mt-1 leading-relaxed line-clamp-3">{s.snippet}</p>
                    )}
                    {s.url && (
                      <p className="text-[11px] text-zinc-400 mt-1 truncate">{s.url}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </motion.section>
      )}

      <motion.section variants={item} className="pt-6 border-t border-zinc-200">
        <p className="text-xs text-zinc-500">
          Report generated by velo2one. Grounded with {snippets.length} live web signals
          {citedNumbers.length > 0 ? `; ${citedNumbers.length} cited above.` : "."}
          {" "}Honest analysis, not investment advice.
        </p>
      </motion.section>
    </motion.div>
  );
}

/* ---------- sub-components ---------- */
function Tag({ children }) {
  return (
    <span className="rounded-full bg-zinc-900 text-white px-3 py-1 text-xs font-semibold">
      {children}
    </span>
  );
}

function SectionHeader({ n, title, sub }) {
  return (
    <div className="flex items-baseline gap-4">
      <span className="text-xs font-mono text-[#FF4757]">{n}</span>
      <div>
        <h2 className="font-serif-display text-3xl md:text-4xl text-zinc-900 leading-tight">{title}</h2>
        {sub && <p className="text-sm text-zinc-600 mt-1 max-w-2xl">{sub}</p>}
      </div>
    </div>
  );
}

function B2CPersona({ p, snippets }) {
  return (
    <div className="velo-card p-8 md:p-10" data-testid="b2c-persona">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-12 h-12 rounded-full bg-[#FF4757]/10 flex items-center justify-center text-[#FF4757]">
          <User className="w-6 h-6" />
        </span>
        <h3 className="font-bold text-2xl">{p.name}</h3>
      </div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PersonaField label="Demographics" value={p.demographics} snippets={snippets} />
        <PersonaField label="Pain intensity" value={p.pain_intensity} snippets={snippets} />
        <PersonaField label="Budget reality" value={p.budget_reality} snippets={snippets} />
        <PersonaField label="Why underserved" value={p.why_underserved} snippets={snippets} />
      </dl>
    </div>
  );
}
function PersonaField({ label, value, snippets }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-zinc-500 mb-1">{label}</dt>
      <dd className="text-sm text-zinc-800 leading-relaxed">
        <Cited snippets={snippets}>{value}</Cited>
      </dd>
    </div>
  );
}

function B2BCommittee({ committee, firm, snippets }) {
  return (
    <div className="space-y-6" data-testid="b2b-committee">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {committee.map((c, i) => (
          <div key={c.role || `role-${i}`} className="velo-card p-6">
            <span className="w-10 h-10 rounded-full bg-[#1E90FF]/10 flex items-center justify-center text-[#1E90FF] mb-4">
              <Building2 className="w-5 h-5" />
            </span>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">{c.role}</p>
            <p className="text-sm font-semibold text-zinc-900 mb-3">{c.title_examples}</p>
            <div className="space-y-2 text-sm text-zinc-700">
              <p><span className="font-semibold text-zinc-900">Cares about:</span> <Cited snippets={snippets}>{c.cares_about}</Cited></p>
              <p><span className="font-semibold text-zinc-900">Objections:</span> <Cited snippets={snippets}>{c.objections}</Cited></p>
            </div>
          </div>
        ))}
      </div>
      {firm && (
        <div className="velo-card p-6">
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Firmographics</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div><span className="font-semibold">Company size:</span> <Cited snippets={snippets}>{firm.company_size}</Cited></div>
            <div><span className="font-semibold">Industry:</span> <Cited snippets={snippets}>{firm.industry}</Cited></div>
            <div><span className="font-semibold">Tech maturity:</span> <Cited snippets={snippets}>{firm.tech_maturity}</Cited></div>
          </div>
        </div>
      )}
    </div>
  );
}

function B2GCommittee({ committee, flag, snippets }) {
  return (
    <div className="space-y-6" data-testid="b2g-committee">
      {flag && (
        <div className="rounded-2xl border-2 border-[#FFA502]/40 bg-[#FFF4E5] p-5 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#B27300] mt-0.5 shrink-0" />
          <p className="text-sm text-[#7a4f00] leading-relaxed">
            <span className="font-bold">B2G reality check:</span> <Cited snippets={snippets}>{flag}</Cited>
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {committee.map((c, i) => (
          <div key={c.role || `role-${i}`} className="velo-card p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{c.role}</p>
            <p className="text-sm text-zinc-800 leading-relaxed"><Cited snippets={snippets}>{c.detail}</Cited></p>
          </div>
        ))}
      </div>
    </div>
  );
}

function MarketBlock({ market, snippets }) {
  if (!market || !market.markets?.length) return null;
  const factorLabels = {
    existing_spend: "Existing spend",
    purchasing_power: "Purchasing power",
    category_maturity: "Category maturity",
    competitive_density: "Competitive density",
    regulatory_ease: "Regulatory ease",
  };
  return (
    <div className="mt-6 space-y-6">
      {market.markets.map((m, idx) => {
        const chartData = Object.entries(factorLabels).map(([k, l]) => ({
          factor: l,
          score: ratingScore(m.factors?.[k]?.rating),
        }));
        return (
          <div key={idx} className="velo-card p-6 md:p-8" data-testid={`market-card-${idx}`}>
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-[#FF4757]" />
                  <h3 className="font-bold text-xl">{m.name}</h3>
                </div>
                {market.recommended_market === m.name && (
                  <span className="rounded-full label-green px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider">Recommended</span>
                )}
              </div>
              <p className="text-sm text-zinc-700 max-w-md leading-relaxed"><Cited snippets={snippets}>{m.verdict}</Cited></p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              <div className="md:col-span-2 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="75%">
                    <PolarGrid stroke="#e4e4e7" />
                    <PolarAngleAxis dataKey="factor" tick={{ fontSize: 10, fill: "#52525b" }} />
                    <PolarRadiusAxis angle={30} domain={[0, 3]} tick={false} axisLine={false} />
                    <Tooltip />
                    <Radar dataKey="score" stroke="#FF4757" fill="#FF4757" fillOpacity={0.3} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="md:col-span-3 space-y-3">
                {Object.entries(factorLabels).map(([k, l]) => {
                  const f = m.factors?.[k];
                  return (
                    <div key={k} className="flex items-start gap-3">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        f?.rating === "strong" ? "label-green" : f?.rating === "moderate" ? "label-amber" : "label-red"
                      }`}>
                        {f?.rating || "n/a"}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-zinc-900">{l}</p>
                        <p className="text-xs text-zinc-600 leading-relaxed"><Cited snippets={snippets}>{f?.note}</Cited></p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PricingBlock({ pricing, bt, snippets }) {
  if (!pricing) return null;
  return (
    <div className="mt-6 space-y-6" data-testid="pricing-block">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="velo-card p-6">
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Suggested model</p>
          <p className="font-bold text-2xl text-zinc-900 capitalize">{pricing.model_recommendation}</p>
          <p className="text-sm text-zinc-600 mt-2"><Cited snippets={snippets}>{pricing.model_reason}</Cited></p>
        </div>
        <div className="velo-card p-6">
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Value ceiling</p>
          <p className="text-sm text-zinc-800 leading-relaxed"><Cited snippets={snippets}>{pricing.value_ceiling}</Cited></p>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${pricing.tiers?.length >= 3 ? "md:grid-cols-3" : pricing.tiers?.length === 2 ? "md:grid-cols-2" : ""} gap-5`}>
        {(pricing.tiers || []).map((t, i) => (
          <div key={t.name || `tier-${i}`} className={`velo-card p-6 relative ${i === 1 && pricing.tiers.length >= 2 ? "ring-2 ring-[#FF4757]" : ""}`} data-testid={`pricing-tier-${i}`}>
            {i === 1 && pricing.tiers.length >= 2 && (
              <span className="absolute -top-3 left-6 rounded-full bg-[#FF4757] text-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider">Sweet spot</span>
            )}
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">{t.target_buyer}</p>
            <h3 className="font-bold text-xl mb-4">{t.name}</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-[11px] text-zinc-500 uppercase tracking-widest mb-0.5">Industry standard</p>
                <p className="font-semibold text-zinc-700">{t.industry_standard_range}</p>
              </div>
              <div>
                <p className="text-[11px] text-[#FF4757] uppercase tracking-widest mb-0.5 font-bold">velo2one suggested</p>
                <p className="font-bold text-lg text-zinc-900">{t.velo21_suggested_range}</p>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed pt-2 border-t border-zinc-100">
                <Cited snippets={snippets}>{t.differentiation_justification}</Cited>
              </p>
            </div>
          </div>
        ))}
      </div>

      {pricing.outcome_based_option && bt !== "B2C" && (
        <div className="velo-card p-6 border-2 border-[#1E90FF]/20 bg-[#1E90FF]/[0.03]">
          <p className="text-xs uppercase tracking-widest text-[#1E90FF] font-bold mb-2">Outcome-based option</p>
          <p className="text-sm text-zinc-800 leading-relaxed"><Cited snippets={snippets}>{pricing.outcome_based_option}</Cited></p>
        </div>
      )}
    </div>
  );
}

function CompetitionBlock({ c, snippets }) {
  if (!c) return null;

  const named = Array.isArray(c.named_competitors) ? c.named_competitors : [];
  const hasNamed = named.length > 0;
  const ue = c.unit_economics || null;
  const compliance = c.compliance || null;

  // Section-scoped sources: only snippet numbers cited inside `competition`
  const localCited = collectCitedNumbers(c);

  return (
    <div className="mt-6 space-y-6" data-testid="competition-block">
      {/* 1. Named competitors — new deep block */}
      {hasNamed ? (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-[#FF4757]" />
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Named competitors</p>
            <span className="rounded-full bg-zinc-900 text-white px-2 py-0.5 text-[10px] font-bold ml-1">{named.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {named.map((n, i) => (
              <NamedCompetitorCard key={n.name || `comp-${i}`} n={n} idx={i} snippets={snippets} />
            ))}
          </div>
        </div>
      ) : (
        // Backward-compat: older reports lacked named_competitors — fall back to the old direct + indirect blocks.
        <LegacyCompetitionBlocks c={c} snippets={snippets} />
      )}

      {/* 2. Unit economics */}
      {ue && (ue.industry_benchmark || (ue.company_specific || []).length > 0) && (
        <div className="velo-card p-6" data-testid="unit-economics">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[#1E90FF]" />
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Margins &amp; unit economics</p>
          </div>
          {ue.company_specific?.length > 0 && (
            <div className="space-y-2 mb-4">
              {ue.company_specific.map((r, i) => (
                <div key={r.company || `ue-${i}`} className="flex items-start gap-3 text-sm text-zinc-800">
                  <span className="font-semibold text-zinc-900 shrink-0">{r.company}:</span>
                  <span className="leading-relaxed"><Cited snippets={snippets}>{r.note}</Cited></span>
                </div>
              ))}
            </div>
          )}
          {ue.industry_benchmark && (
            <div className="rounded-xl bg-[#1E90FF]/[0.04] border border-[#1E90FF]/20 p-4">
              <p className="text-[11px] uppercase tracking-widest text-[#1E90FF] font-bold mb-1">Industry benchmark</p>
              <p className="text-sm text-zinc-800 leading-relaxed">
                <Cited snippets={snippets}>{ue.industry_benchmark}</Cited>
              </p>
            </div>
          )}
        </div>
      )}

      {/* 3. Compliance / legal angle */}
      {compliance && (compliance.items || []).length > 0 && (
        <div className="velo-card p-6 border-2 border-[#FFA502]/30 bg-[#FFF9EF]" data-testid="compliance-block">
          <div className="flex items-start gap-3 mb-3">
            <ShieldCheck className="w-5 h-5 text-[#B27300] mt-0.5 shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-widest text-[#B27300] font-bold">Compliance &amp; legal angle</p>
              <p className="text-xs text-[#7a4f00] mt-0.5">
                {compliance.disclaimer || "This is a checklist to raise with a lawyer, not legal advice."}
              </p>
            </div>
          </div>
          <ul className="space-y-3 mt-4">
            {compliance.items.map((item, i) => (
              <li key={item.title || `comp-item-${i}`} className="flex items-start gap-3">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#FFA502] shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-zinc-900">{item.title}</p>
                  <p className="text-xs text-zinc-700 leading-relaxed mt-0.5">
                    <Cited snippets={snippets}>{item.detail}</Cited>
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 4. Positioning gaps (kept) */}
      <div className="velo-card p-6">
        <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">Positioning gaps</p>
        <ul className="space-y-2">
          {(c.positioning_gaps || []).map((g, i) => (
            <li key={typeof g === "string" ? `gap-${i}-${g.slice(0, 30)}` : `gap-${i}`} className="flex items-start gap-3 text-sm text-zinc-800">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFA502] mt-2 shrink-0" />
              <span className="leading-relaxed"><Cited snippets={snippets}>{g}</Cited></span>
            </li>
          ))}
        </ul>
      </div>

      {/* 5. Founder wedge (kept) */}
      <div className="velo-card p-8 md:p-10 bg-zinc-900 text-white" data-testid="founder-wedge">
        <p className="text-xs uppercase tracking-widest text-[#FF4757] font-bold mb-3">Your wedge</p>
        <p className="font-serif-display text-2xl md:text-3xl leading-snug">
          <Cited snippets={snippets}>{c.founder_wedge}</Cited>
        </p>
      </div>

      {/* 6. Section-scoped Sources — only what's cited inside Competition */}
      {localCited.length > 0 && (
        <div className="velo-card p-6" data-testid="competition-sources">
          <div className="flex items-center gap-2 mb-3">
            <ExternalLink className="w-4 h-4 text-zinc-500" />
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Competition sources</p>
          </div>
          <ol className="space-y-2" data-testid="competition-sources-list">
            {localCited.map((n) => {
              const s = snippets?.[n - 1];
              if (!s) return null;
              return (
                <li key={n} className="flex items-start gap-2 text-sm" data-testid={`competition-source-${n}`}>
                  <span className="font-mono text-[#FF4757] font-bold shrink-0 text-xs pt-1">[{n}]</span>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-zinc-800 hover:text-[#FF4757] transition-colors inline-flex items-start gap-1"
                  >
                    <span className="font-semibold">{s.title || s.url}</span>
                    <ExternalLink className="w-3 h-3 mt-0.5 opacity-60 shrink-0" />
                  </a>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}

function NamedCompetitorCard({ n, idx, snippets }) {
  const motion = (n.sales_motion || "").toLowerCase();
  const motionClass =
    motion.includes("self") || motion.includes("plg") ? "label-green" :
    motion.includes("enterprise") || motion.includes("sales-led") ? "label-amber" :
    "label-green";
  return (
    <div className="velo-card p-5 h-full flex flex-col" data-testid={`named-competitor-${idx}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-bold text-base text-zinc-900">
          {n.url ? (
            <a href={n.url} target="_blank" rel="noreferrer noopener" className="hover:text-[#FF4757] transition-colors inline-flex items-center gap-1">
              {n.name}
              <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
          ) : (
            n.name
          )}
        </h4>
        {n.sales_motion && (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shrink-0 ${motionClass}`}>
            {n.sales_motion}
          </span>
        )}
      </div>

      <div className="space-y-2 text-sm text-zinc-700 flex-1">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">Positioning</p>
          <p className="leading-relaxed"><Cited snippets={snippets}>{n.target_positioning}</Cited></p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">Pricing</p>
          <p className="leading-relaxed"><Cited snippets={snippets}>{n.pricing}</Cited></p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">Traction</p>
          <p className="leading-relaxed"><Cited snippets={snippets}>{n.traction_signal}</Cited></p>
        </div>
      </div>
    </div>
  );
}

function LegacyCompetitionBlocks({ c, snippets }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="legacy-competition">
      <div className="velo-card p-6">
        <div className="flex items-center gap-2 mb-4"><Target className="w-4 h-4 text-[#FF4757]" /><p className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Direct competitors</p></div>
        <div className="space-y-3">
          {(c.direct || []).map((d, i) => (
            <div key={d.name || `dc-${i}`} className="border-l-2 border-[#FF4757] pl-4">
              <p className="font-bold text-sm text-zinc-900">{d.name}</p>
              <p className="text-xs text-zinc-600"><span className="font-semibold">Approach:</span> <Cited snippets={snippets}>{d.approach}</Cited></p>
              <p className="text-xs text-zinc-600"><span className="font-semibold">Weakness:</span> <Cited snippets={snippets}>{d.weakness}</Cited></p>
            </div>
          ))}
        </div>
      </div>

      <div className="velo-card p-6">
        <div className="flex items-center gap-2 mb-4"><Layers className="w-4 h-4 text-[#1E90FF]" /><p className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Indirect / substitutes</p></div>
        <div className="space-y-3">
          {(c.indirect_substitutes || []).map((d, i) => (
            <div key={d.name || `ic-${i}`} className="border-l-2 border-[#1E90FF] pl-4">
              <p className="font-bold text-sm text-zinc-900">{d.name}</p>
              <p className="text-xs text-zinc-600"><Cited snippets={snippets}>{d.why_used}</Cited></p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StrengthFraming({ validation }) {
  if (!validation) return null;
  const labels = [
    validation?.problem_realness?.label,
    validation?.monetization_signal?.label,
    validation?.differentiation?.label,
  ].map((l) => (l || "").toLowerCase());
  const hasRed = labels.includes("red");
  const hasAmber = labels.includes("amber");

  let title;
  let body;
  if (hasRed) {
    title = "Some signals are less proven yet.";
    body = (
      <>
        That&rsquo;s useful information, not a stop sign. Below is your full go-to-market plan
        {" \u2014 "}ICP, target market, pricing, competition{" \u2014 "}
        framed as &ldquo;here&rsquo;s how to move forward anyway&rdquo;.
      </>
    );
  } else if (hasAmber) {
    title = "Mixed signals \u2014 still plenty to work with.";
    body = (
      <>
        Treat the amber notes as things to pressure-test as you build. Below is your full ICP,
        market, pricing and competition plan for taking this to market from here.
      </>
    );
  } else {
    title = "Strong signals across the board.";
    body = (
      <>
        Now let&rsquo;s turn that into a route. Below is your full ICP, market, pricing and
        competition plan.
      </>
    );
  }
  return (
    <div
      className="mt-6 rounded-2xl border border-zinc-200 bg-white p-6 md:p-7 flex items-start gap-4"
      data-testid="strength-framing"
    >
      <span className="mt-1 w-2 h-2 rounded-full bg-[#FF4757] shrink-0" />
      <div>
        <p className="font-bold text-zinc-900">{title}</p>
        <p className="text-sm text-zinc-700 leading-relaxed mt-1">{body}</p>
      </div>
    </div>
  );
}
